import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import DieSubCategoryListService from '../services/die-subcategorylist.service';
import { setMessage } from './message';

export const fetchDieSubCategoryList = createAsyncThunk(
  'dieTool/fetchDieSubCategoryList',
  async ({ page, pagesize, search, ordering }, thunkAPI) => {
    try {
      const response = await DieSubCategoryListService.getDieSubCategoryList(page, pagesize, search, ordering);
      return response.data;
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue();
    }
  }
);
export const fetchDieSubCategoryListDropdown = createAsyncThunk(
  'dieTool/fetchDieSubCategoryListDropdown',
  async ({ page, pagesize, search, ordering }, thunkAPI) => {
    try {
      const response = await DieSubCategoryListService.getDieSubCategoryListDropdown(page, pagesize, search, ordering);
      return response.data;
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue();
    }
  }
);
export const fetchArchivedDieSubCategoryList = createAsyncThunk(
  'dieTool/fetchArchiveDieSubCategoryList',
  async ({ page, pagesize, search, ordering }, thunkAPI) => {
    try {
      const response = await DieSubCategoryListService.getDieArchiveSubCategoryList(page, pagesize, search, ordering);
      return response.data;
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue();
    }
  }
);

export const addDieSubCategory = createAsyncThunk(
  'dieTool/addDieSubCategory',
  async (subcategory, thunkAPI) => {
    try {
      const data = await DieSubCategoryListService.addDieSubCategory(subcategory);
      return data;
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const editDieSubCategory = createAsyncThunk(
  'dieTool/editDieSubCategory',
  async ({ id, updatedData }, thunkAPI) => {
    try {
      const response = await DieSubCategoryListService.editDieSubCategory(id, updatedData);
      return response.data;
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const deleteDieSubCategory = createAsyncThunk(
  'dieTool/deleteDieSubCategory',
  async (id, thunkAPI) => {
    try {
      await DieSubCategoryListService.deleteDieSubCategory(id);
      return id;  // Return the ID to update the state
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const unArchiveDieSubCategory = createAsyncThunk(
  'dieTool/unArchiveDieSubCategory',
  async (id, thunkAPI) => {
    try {
      await DieSubCategoryListService.unArchiveDieSubCategory(id); // Ensure `id` is used here
      return id; // Return the ID of the deleted group
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue();
    }
  }
);

export const resetDieSubCategoryListThunk = () => (dispatch) => {
  return new Promise((resolve) => {
    dispatch(resetDieSubCategoryList()); // Dispatch the synchronous action
    resolve(); // Resolve the promise
  });
};

const initialState = {
  data: [],
  list: [],
  loading: false,
  totalPages: 0,
  currentPage: 1,
  totalRecord: 0,
  status: 'idle',
  error: null,
  hasMore: true,
};

const dieSubCategorySlice = createSlice({
  name: 'dieSubCategory',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    resetDieSubCategoryList: (state) => {
      state.list = []; // This will clear the list
    },
    resetCurrentSubCategoryPage: (state) => {
      state.currentPage = 1;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDieSubCategoryList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchDieSubCategoryList.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const fetchedPage = action.payload.page || state.currentPage;
        state.data = action.payload.results.data || [];  // Update to match your API response
        state.totalRecord = action.payload.count || 0; // Handle count (total records)
        state.totalPages = action.payload.totalPages || 0; // Handle totalPages
        state.totaldie = action?.payload?.results?.total_die_count || 0;
        state.currentPage = action.payload.next ? fetchedPage + 1 : fetchedPage;
        state.list = [...state.list, ...action.payload.results.data];
        state.hasMore = !!action.payload.next;
      })
      .addCase(fetchDieSubCategoryList.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.error.message;
      });
    builder
      .addCase(fetchDieSubCategoryListDropdown.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchDieSubCategoryListDropdown.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const fetchedPage = action.payload.page || state.currentPage;
        state.data = action.payload.results.data || [];  // Update to match your API response
        state.totalRecord = action.payload.count || 0; // Handle count (total records)
        state.totalPages = action.payload.totalPages || 0; // Handle totalPages
        state.currentPage = action.payload.next ? fetchedPage + 1 : fetchedPage;
        state.list = [...state.list, ...action.payload.results.data];
        state.hasMore = !!action.payload.next;
      })
      .addCase(fetchDieSubCategoryListDropdown.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.error.message;
      });
    builder
      .addCase(fetchArchivedDieSubCategoryList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchArchivedDieSubCategoryList.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const fetchedPage = action.payload.page || state.currentPage;
        state.data = action.payload.results.data || [];  // Update to match your API response
        state.totalRecord = action.payload.count || 0; // Handle count (total records)
        state.totalPages = action.payload.totalPages || 0; // Handle totalPages
        state.currentPage = action.payload.next ? fetchedPage + 1 : fetchedPage;
        state.list = [...state.list, ...action.payload.results.data];
        state.hasMore = !!action.payload.next;
      })
      .addCase(fetchArchivedDieSubCategoryList.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.error.message;
      });
    builder
      .addCase(addDieSubCategory.pending, state => {
        state.status = 'loading';
      })
      .addCase(addDieSubCategory.fulfilled, (state, action) => {
        state.status = 'succeeded';
      })
      .addCase(addDieSubCategory.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
    builder
      .addCase(editDieSubCategory.pending, state => {
        state.status = 'loading';
      })
      .addCase(editDieSubCategory.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const updatedGroup = action.payload;
        const index = state.data.findIndex(
          group => group.id === updatedGroup.id,
        ); // Find the index of the updated group
        if (index !== -1) {
          state.data[index] = updatedGroup; // Replace the old group with the updated one
        }
      })
      .addCase(editDieSubCategory.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.payload;
      });
    builder
      .addCase(deleteDieSubCategory.pending, state => {
        state.status = 'loading';
      })
      .addCase(deleteDieSubCategory.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.data = state.data.filter(group => group.id !== action.payload); // Remove the deleted group from state
        state.list = state.list.filter(group => group.id !== action.payload); // Remove the deleted group from list
      })
      .addCase(deleteDieSubCategory.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.payload;
      });
    builder
      .addCase(unArchiveDieSubCategory.pending, state => {
        state.status = 'loading';
      })
      .addCase(unArchiveDieSubCategory.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.data = state.data.filter(group => group.id !== action.payload); // Remove the deleted group from state
      })
      .addCase(unArchiveDieSubCategory.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.error.message;
      });
  },
});


export const { resetDieSubCategoryList, resetCurrentSubCategoryPage } = dieSubCategorySlice.actions;
const { reducer } = dieSubCategorySlice;
export default reducer;
